// ./components/Customers/CustomersForm.js
import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Modal, Row, Button, Icon } from 'antd';
import { Formik, Field, Form } from 'formik';
import * as Yup from 'yup';

import nltsjson from './nationalities.json';
import { AntInput, AntSelect, AntInputNumber } from '../AntField';

const formStyles = `
@keyframes cf-float {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-4px); }
}
@keyframes cf-beat {
  0%, 100% { transform: scale(1); }
  50%       { transform: scale(1.22); }
}
@keyframes cf-bounce {
  0%, 100% { transform: translateY(0px) scaleY(1); }
  40%       { transform: translateY(-5px) scaleY(1.05); }
  70%       { transform: translateY(2px) scaleY(0.96); }
}
@keyframes cf-glow {
  0%, 100% { box-shadow: 0 0 0 0 var(--gc, rgba(24,144,255,0.35)); }
  50%       { box-shadow: 0 0 0 8px rgba(0,0,0,0); }
}
@keyframes modal-bar {
  0%   { background-position: 0% center; }
  100% { background-position: 200% center; }
}

.cf-float  { animation: cf-float  3s   ease-in-out infinite; }
.cf-beat   { animation: cf-beat   1.8s ease-in-out infinite; }
.cf-bounce { animation: cf-bounce 2.2s ease-in-out infinite; }
.cf-glow   { animation: cf-glow   2.4s ease-in-out infinite; }

.cf-modal .ant-modal-content {
  border-radius: 14px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 64px rgba(0,0,0,0.15), 0 0 0 1px rgba(24,144,255,0.1) !important;
}
.cf-modal .ant-modal-header {
  padding: 0 !important;
  border-bottom: none !important;
  border-radius: 14px 14px 0 0 !important;
  overflow: hidden !important;
}
.cf-modal .ant-modal-body {
  padding: 24px 28px 12px !important;
}
.cf-modal .ant-modal-close {
  top: 14px !important; right: 20px !important;
  color: rgba(255,255,255,0.8) !important;
  transition: color 0.2s, transform 0.2s !important;
}
.cf-modal .ant-modal-close:hover {
  color: #fff !important; transform: rotate(90deg) !important;
}
.cf-modal .ant-modal-close .ant-modal-close-x {
  width: 40px !important; height: 40px !important; line-height: 40px !important;
}

.cf-modal-header {
  background: linear-gradient(135deg, #003a8c 0%, #0050b3 35%, #1890ff 70%, #096dd9 100%);
  padding: 22px 28px 20px;
  display: flex; align-items: center; gap: 16px;
  position: relative; overflow: hidden;
}
.cf-modal-header::before {
  content: '';
  position: absolute; inset: 0;
  background-image: radial-gradient(rgba(255,255,255,0.09) 1px, transparent 1px);
  background-size: 20px 20px; pointer-events: none;
}
.cf-modal-header::after {
  content: '';
  position: absolute; bottom: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, #13c2c2, #1890ff, #722ed1, #eb2f96, #13c2c2);
  background-size: 300% auto; animation: modal-bar 3s linear infinite;
}
.cf-modal-icon {
  width: 52px; height: 52px; border-radius: 14px;
  background: rgba(255,255,255,0.15);
  border: 1.5px solid rgba(255,255,255,0.25);
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.cf-modal-title { font-size: 18px; font-weight: 800; color: #fff; letter-spacing: 1px; }
.cf-modal-sub   { font-size: 12px; color: rgba(255,255,255,0.65); letter-spacing: 1.5px; font-weight: 500; text-transform: uppercase; }

.ant-modal-footer {
  margin: 12px -28px 0 !important;
  padding: 14px 28px 20px !important;
  border-top: 1px solid #f0f0f0;
}

.ant-modal-footer .ant-btn {
  height: 38px;
  padding: 0 20px;
  font-size: 14px;
  border-radius: 8px;
}

.ant-modal-footer .ant-btn-primary {
  background: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
  border: none;
}

/* Override global CSS na nag-cacause ng vertical spacing */
.cf-modal .ant-row {
  margin-left: -8px !important;
  margin-right: -8px !important;
}

.cf-modal .ant-col {
  padding-left: 8px !important;
  padding-right: 8px !important;
  margin-bottom: 0 !important;
}

.cf-modal .ant-form-item {
  margin-bottom: 16px !important;
}
`;

const validateForm = Yup.object().shape({
  bi_name: Yup.string()
    .required('Business name is required')
    .matches(/^[a-zA-Z0-9-_ ]*$/, 'Special characters are not allowed')
    .transform(cv => cv ? cv : '').max(100),
  bi_address: Yup.string()
    .required('Business address is required')
    .transform(cv => cv ? cv : '').max(150),
  bi_nature: Yup.string()
    .required('Nature of business is required')
    .transform(cv => cv ? cv : '').max(150),
  bi_type: Yup.string()
    .required('Business type is required')
    .transform(cv => cv ? cv : ''),
  bi_yearsBusiness: Yup.number()
    .transform(cv => isNaN(cv) ? undefined : cv)
    .integer('Decimal is not allowed')
    .positive('Must be positive')
    .required('Years in business is required'),
  bi_contact: Yup.string()
    .required('Contact person is required')
    .matches(/^[a-zA-Z ]*$/, 'Only Alphabets are allowed')
    .transform(cv => cv ? cv : '').max(50, 'Must not exceed 50 characters'),
  bi_contactposition: Yup.string()
    .when('bi_contact', {
      is: value => value !== undefined && value !== '',
      then: Yup.string().required('Contact person position is required'),
      otherwise: Yup.string().nullable()
    })
    .transform(cv => cv ? cv : '').max(50),
  bi_tel: Yup.string()
    .matches(/^[0-9-]+$/, 'Input a valid telephone number')
    .required('Telephone number is required')
    .transform(cv => cv ? cv : '').max(20),
  bi_fax: Yup.string()
    .matches(/^[0-9-]+$/, 'Input a valid fax number')
    .nullable().max(20),
  bi_sss: Yup.string().max(30, 'Must not exceed 50 characters').nullable(),
  bi_tin: Yup.string().max(30, 'Must not exceed 50 characters').nullable(),
  bi_email: Yup.string().email('Email is not valid').nullable().max(50),
  bi_nationality: Yup.string().required('Owner nationality is required'),
});

const nationalityOptions = nltsjson.map(n => n.name);

// FormCacheManager class
class FormCacheManager {
  constructor(dbName = 'CustomersFormCache', storeName = 'form_drafts', maxAgeHours = 24) {
    this.dbName = dbName;
    this.storeName = storeName;
    this.maxAgeHours = maxAgeHours;
    this.db = null;
    this.isInitialized = false;
  }

  async init() {
    if (this.isInitialized && this.db) return this.db;
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, 1);
      
      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        this.db = request.result;
        this.isInitialized = true;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'key' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('expiry', 'expiry', { unique: false });
        }
      };
    });
  }

  async saveDraft(key, data) {
    try {
      await this.init();
      
      const expiry = Date.now() + (this.maxAgeHours * 60 * 60 * 1000);
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        
        const cacheEntry = {
          key: key,
          data: data,
          timestamp: Date.now(),
          expiry: expiry
        };
        
        const request = store.put(cacheEntry);
        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.warn('Form draft save error:', error);
      return false;
    }
  }

  async getDraft(key) {
    try {
      await this.init();
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([this.storeName], 'readonly');
        const store = transaction.objectStore(this.storeName);
        const request = store.get(key);
        
        request.onsuccess = () => {
          const result = request.result;
          if (result && result.expiry && result.expiry > Date.now()) {
            resolve(result.data);
          } else if (result) {
            this.deleteDraft(key);
            resolve(null);
          } else {
            resolve(null);
          }
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      return null;
    }
  }

  async deleteDraft(key) {
    try {
      await this.init();
      
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        const request = store.delete(key);
        request.onsuccess = () => resolve(true);
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      return false;
    }
  }
}

function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const CustomersForm = ({ 
  visible, 
  data, 
  closeModal, 
  addCustomer, 
  updateCustomer, 
  setLoading 
}) => {
  const initialValue = useRef(null);
  const formCache = useRef(null);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [formValues, setFormValues] = useState(null);
  const debouncedFormValues = useDebounce(formValues, 2000);
  const draftKey = useRef(null);
  
  // SAME SIZE AS OLD MODAL - pero slightly smaller para hindi masyadong malapad
  const modalWidth = 1000;

  useEffect(() => {
    formCache.current = new FormCacheManager();
    formCache.current.init();
  }, []);

  if (Object.prototype.hasOwnProperty.call(data, 'id')) {
    initialValue.current = data;
  } else {
    initialValue.current = {
      bi_name: '', bi_address: '', bi_nature: '',
      bi_premises: 'OWNED', bi_type: '',
      bi_yearsBusiness: '', bi_contact: '',
      bi_contactposition: '', bi_tel: '', bi_fax: '',
      bi_sss: '', bi_tin: '', bi_email: '', bi_nationality: '',
    };
  }

  const isEdit = Object.prototype.hasOwnProperty.call(data, 'id');
  draftKey.current = `customer_form_${isEdit ? 'edit_' + data.id : 'new'}`;

  useEffect(() => {
    if (visible && !isEdit && formCache.current) {
      const loadDraft = async () => {
        const draft = await formCache.current.getDraft(draftKey.current);
        if (draft && Object.keys(draft).some(key => draft[key])) {
          const hasValues = Object.values(draft).some(val => val && val !== '');
          if (hasValues) {
            // message.info('📝 Restored saved draft', 2);
          }
        }
      };
      loadDraft();
    }
  }, [visible, isEdit]);

  useEffect(() => {
    const saveDraftAsync = async () => {
      if (!isEdit && formCache.current && debouncedFormValues) {
        const hasValues = Object.values(debouncedFormValues).some(val => val && val !== '');
        if (hasValues) {
          await formCache.current.saveDraft(draftKey.current, debouncedFormValues);
        }
      }
    };
    
    saveDraftAsync();
  }, [debouncedFormValues, isEdit]);

  const clearDraft = async () => {
    if (!isEdit && formCache.current) {
      await formCache.current.deleteDraft(draftKey.current);
    }
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleFormValuesChange = useCallback((values) => {
    setFormValues(values);
  }, []);

  return (
    <>
      <style>{formStyles}</style>
      <Modal
        className="cf-modal"
        visible={visible}
        style={{ top: 10 }}
        width={modalWidth}
        footer={null}
        onCancel={closeModal}
        destroyOnClose
        title={
          <div className="cf-modal-header">
            <div className={`cf-modal-icon cf-glow ${isEdit ? 'cf-beat' : 'cf-float'}`}
              style={{ '--gc': isEdit ? 'rgba(19,194,194,0.4)' : 'rgba(24,144,255,0.4)' }}>
              <Icon type={isEdit ? 'edit' : 'user-add'} style={{ fontSize: 24, color: '#fff' }} />
            </div>
            <div>
              <div className="cf-modal-title">
                {isEdit ? (data.bi_name || 'Edit Customer') : 'New Customer'}
              </div>
              <div className="cf-modal-sub">
                {isEdit ? 'Update Customer Details' : 'Add Customer Details'}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', flexShrink: 0 }}>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                background: isEdit ? 'rgba(19,194,194,0.2)' : 'rgba(82,196,26,0.2)',
                border: `1px solid ${isEdit ? 'rgba(19,194,194,0.4)' : 'rgba(82,196,26,0.4)'}`,
                borderRadius: 24, padding: '4px 12px',
                fontSize: 12, fontWeight: 700,
                color: isEdit ? '#13c2c2' : '#52c41a', letterSpacing: '0.8px',
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: isEdit ? '#13c2c2' : '#52c41a',
                  boxShadow: isEdit ? '0 0 8px #13c2c2' : '0 0 8px #52c41a',
                }} />
                {isEdit ? 'EDITING' : 'NEW'}
              </span>
            </div>
          </div>
        }
      >
        {!isOnline && (
          <div style={{
            background: '#fff1f0',
            border: '1px solid #ffccc7',
            borderRadius: 10,
            padding: '10px 16px',
            marginBottom: 20,
            fontSize: 12,
            color: '#ff4d4f',
            display: 'flex',
            alignItems: 'center',
            gap: 10
          }}>
            <Icon type="disconnect" style={{ fontSize: 16 }} />
            <span>You are offline. Form will be saved as draft and submitted when connection resumes.</span>
          </div>
        )}

        <Formik
          initialValues={initialValue.current}
          validationSchema={validateForm}
          onSubmit={async (values, { setSubmitting, resetForm }) => {
            await clearDraft();
            
            const onSuccess = () => {
              closeModal();
              resetForm();
            };
            
            if (Object.prototype.hasOwnProperty.call(values, 'id')) {
              await updateCustomer(values, setLoading, closeModal, setSubmitting, onSuccess);
            } else {
              await addCustomer(values, setLoading, resetForm, setSubmitting, onSuccess);
            }
          }}
          enableReinitialize
        >
          {({ values, isSubmitting, submitCount, handleReset, handleSubmit }) => {
            if (values && !isEdit && !isSubmitting) {
              setTimeout(() => {
                handleFormValuesChange(values);
              }, 0);
            }
            
            return (
              <Form>
                {/* ITO YUNG OLD LAYOUT - HINDI VERTICAL */}
                <div style={{ display: 'flex', gap: '24px' }}>
                  {/* LEFT COLUMN */}
                  <div style={{ flex: 1 }}>
                    <Field
                      component={AntInput}
                      label="COMPANY NAME"
                      name="bi_name"
                      value={values.bi_name}
                      submitCount={submitCount}
                      placeholder="Required field"
                    />

                    <Field
                      component={AntInput}
                      label="COMPANY ADDRESS"
                      name="bi_address"
                      value={values.bi_address}
                      submitCount={submitCount}
                      placeholder="Required field"
                    />

                    <Field
                      component={AntInput}
                      label="NATURE OF BUSINESS"
                      name="bi_nature"
                      value={values.bi_nature}
                      submitCount={submitCount}
                      placeholder="Required field"
                    />

                    <Field
                      component={AntSelect}
                      label="BUSINESS PREMISES"
                      name="bi_premises"
                      value={values.bi_premises}
                      selectOptions={['OWNED', 'RENTED']}
                      submitCount={submitCount}
                    />

                    <Field
                      component={AntSelect}
                      label="BUSINESS TYPE"
                      name="bi_type"
                      value={values.bi_type}
                      selectOptions={['CORPORATION', 'SINGLE PROPRIETORSHIP', 'PARTNERSHIP']}
                      submitCount={submitCount}
                    />

                    <Field
                      component={AntInputNumber}
                      label="NO. OF YEARS IN BUSINESS"
                      name="bi_yearsBusiness"
                      value={values.bi_yearsBusiness}
                      submitCount={submitCount}
                      min={1}
                    />

                    <Field
                      component={AntInput}
                      label="TELEPHONE NO."
                      name="bi_tel"
                      value={values.bi_tel}
                      submitCount={submitCount}
                      placeholder="Required field"
                    />
                  </div>

                  {/* RIGHT COLUMN */}
                  <div style={{ flex: 1 }}>
                    <Field
                      component={AntInput}
                      label="FAX NO."
                      name="bi_fax"
                      value={values.bi_fax}
                      submitCount={submitCount}
                    />

                    <Field
                      component={AntInput}
                      label="SSS NO."
                      name="bi_sss"
                      value={values.bi_sss}
                      submitCount={submitCount}
                    />
                    
                    <Field
                      component={AntInput}
                      label="TIN NO."
                      name="bi_tin"
                      value={values.bi_tin}
                      submitCount={submitCount}
                    />

                    <Field
                      component={AntInput}
                      label="EMAIL ADDRESS"
                      name="bi_email"
                      value={values.bi_email}
                      submitCount={submitCount}
                    />

                    <Field
                      component={AntInput}
                      label="CONTACT PERSON"
                      name="bi_contact"
                      value={values.bi_contact}
                      submitCount={submitCount}
                      placeholder="Required field"
                    />

                    <Field
                      component={AntInput}
                      label="POSITION"
                      name="bi_contactposition"
                      value={values.bi_contactposition}
                      submitCount={submitCount}
                    />

                    <Field
                      component={AntSelect}
                      label="OWNER NATIONALITY"
                      name="bi_nationality"
                      value={values.bi_nationality}
                      selectOptions={nationalityOptions}
                      submitCount={submitCount}
                      filterOption={(input, option) =>
                        option && option.props && option.props.children && 
                        option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                      showSearch
                      placeholder="Search or select nationality"
                    />
                  </div>
                </div>

                <div className="ant-modal-footer">
                  <Button icon="reload" onClick={handleReset} disabled={isSubmitting}>
                    Reset
                  </Button>
                  <Button 
                    icon="save" 
                    disabled={isSubmitting} 
                    type="primary" 
                    onClick={handleSubmit}
                    loading={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit'}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

CustomersForm.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  visible: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  addCustomer: PropTypes.func.isRequired,
  updateCustomer: PropTypes.func.isRequired,
};

export default CustomersForm;